// resources/js/tracking/map-tracking.js
//
// Session state + geo cache helpers for the Phase 2 Activation funnel.
//
// - Session counter (sessionStorage): incremented at each map mount. Resets on tab close.
// - First-time flag (localStorage): set once on the very first Map Opened
//   of this device, then never again.
// - First Map Opened timestamp (localStorage): used for days_since_first_open.
// - Last Map Opened timestamp (localStorage): used for days_since_last_open.
// - Map Interaction fired flag (sessionStorage): debounces Map Interaction
//   to fire at most once per tab session.
// - Current geo (in-memory): updated asynchronously by the map's moveend
//   handler; read synchronously when firing events. May be null for the
//   very first Map Opened before the initial geo fetch resolves.

const SESSION_KEY = 'mh_map_session_count';
const FIRST_OPEN_KEY = 'mh_first_map_opened_at';
const LAST_OPEN_KEY = 'mh_last_map_opened_at';
const INTERACTION_FIRED_KEY = 'mh_map_interaction_fired';

let currentGeo = { country: null, region: null, dept: null };

/**
 * Increment the map session counter (per browser session, resets on tab close).
 * Returns the new value AFTER increment.
 */
export function incrementSessionCount() {
  if (typeof window === 'undefined' || !window.sessionStorage) return 1;
  const current = parseInt(window.sessionStorage.getItem(SESSION_KEY) || '0', 10);
  const next = current + 1;
  window.sessionStorage.setItem(SESSION_KEY, String(next));
  return next;
}

/**
 * Was this the first map-open of this browser session? True only when
 * the sessionStorage counter is 1 at call time.
 */
export function isFirstSessionOpen(sessionCount) {
  return sessionCount === 1;
}

/**
 * First-time across the device (localStorage). Call AFTER incrementing
 * the session counter — this will set-if-not-exists the first-open
 * timestamp and return true only the very first time.
 */
export function isDeviceFirstTime() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  if (window.localStorage.getItem(FIRST_OPEN_KEY)) {
    return false;
  }
  window.localStorage.setItem(FIRST_OPEN_KEY, new Date().toISOString());
  return true;
}

/**
 * Days elapsed since the first Map Opened on this device. Returns 0 if
 * today is the first open, null if no record exists yet.
 */
export function daysSinceFirstOpen() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = window.localStorage.getItem(FIRST_OPEN_KEY);
  if (!raw) return null;
  const then = new Date(raw).getTime();
  const now = Date.now();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

/**
 * Days since the LAST Map Opened. Returns null if this is the first.
 * Call this BEFORE `recordThisOpen()` so "last" still refers to the
 * previous open.
 */
export function daysSinceLastOpen() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = window.localStorage.getItem(LAST_OPEN_KEY);
  if (!raw) return null;
  const then = new Date(raw).getTime();
  const now = Date.now();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

/**
 * Record that a map was opened now. Updates the "last opened" timestamp.
 * Call this AFTER computing daysSinceLastOpen for the current event.
 */
export function recordThisOpen() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(LAST_OPEN_KEY, new Date().toISOString());
}

/**
 * Mark that Map Interaction has been fired for this session already.
 * Returns true if this was the first call (i.e. fire the event now),
 * false if already fired (i.e. suppress).
 */
export function shouldFireInteractionThisSession() {
  if (typeof window === 'undefined' || !window.sessionStorage) return false;
  if (window.sessionStorage.getItem(INTERACTION_FIRED_KEY)) {
    return false;
  }
  window.sessionStorage.setItem(INTERACTION_FIRED_KEY, '1');
  return true;
}

/**
 * Async geo cache updater. Call from the map's moveend handler with the
 * center coordinates. Fetches /api/geo/reverse and updates currentGeo.
 * Non-blocking — returns a promise the caller doesn't need to await.
 */
export async function updateGeoCache(lat, lng) {
  try {
    const response = await fetch(`/api/geo/reverse?lat=${lat}&lng=${lng}`, {
      headers: { 'Accept': 'application/json' },
    });
    if (!response.ok) return;
    const data = await response.json();
    currentGeo = {
      country: data.country || null,
      region: data.region || null,
      dept: data.dept || null,
    };
  } catch (e) {
    // Silent failure — geo is non-critical enrichment.
  }
}

/**
 * Read the current cached geo. Returns the in-memory snapshot.
 * May be all-null before the first updateGeoCache resolves.
 */
export function getCurrentGeo() {
  return { ...currentGeo };
}
